import { useCallback, useRef } from 'react'
import { Search } from 'lucide-react'
import { useViewContext } from '../../view-context'
import { EmptyState, EmptyStateDescription, EmptyStateIcon } from '../../components/meeting/empty-state'
import { View } from '../../components/view'
import { useReminder } from '../../hooks/use-reminder'
import { useReminderActions } from '../../hooks/use-reminders'
import { useReminderImageAttach } from '../../components/reminders/utils/image-attachments'
import { ReminderDetailActions } from '../../components/reminders/nested/reminder-detail-actions'
import { ReminderNotes } from '../../components/reminders/nested/reminder-notes'
import { ReminderAttachments } from '../../components/reminders/nested/reminder-attachments'
import { ReminderCheckbox } from '../../components/reminders/nested/reminder-checkbox'
import { ReminderTitle } from '../../components/reminders/nested/reminder-title'

const VIEW_TRIGGER = 'reminder-view'

export interface ReminderViewContext {
  reminderId: string
}

/**
 * Detail View for a single reminder, opened from chat references and deeplinks.
 * The same body the row's popover shows — title, notes, moves — since both are
 * the one reminder and a second reading of it would only drift.
 */
export function ReminderView() {
  const { context } = useViewContext<ReminderViewContext>()
  const { reminder, isLoading, error } = useReminder({ reminderId: context?.reminderId })
  const { setResolved } = useReminderActions()
  const attach = useReminderImageAttach(context?.reminderId ?? '', () => {})

  // The arrow keys cross between the title and the notes the way they cross lines within either.
  const titleRef = useRef<HTMLTextAreaElement>(null)
  const notesRef = useRef<HTMLTextAreaElement>(null)
  const focusNotes = useCallback(() => {
    const field = notesRef.current
    field?.focus()
    field?.setSelectionRange(0, 0)
  }, [])
  const focusTitle = useCallback(() => {
    const field = titleRef.current
    field?.focus()
    field?.setSelectionRange(field.value.length, field.value.length)
  }, [])

  const message = error && !reminder ? 'Failed to load reminder' : undefined
  const emptyMessage =
    message ??
    (isLoading
      ? 'Loading reminder…'
      : !reminder
        ? 'Reminder no longer exists'
        : reminder.state === 'dismissed'
          ? 'This reminder was dismissed'
          : undefined)

  const resolved = reminder?.resolved === true

  if (emptyMessage || !reminder) {
    return (
      <View>
        <div className="flex flex-1 flex-col items-center justify-center">
          <EmptyState>
            <EmptyStateIcon>
              <Search />
            </EmptyStateIcon>
            <EmptyStateDescription>{emptyMessage}</EmptyStateDescription>
          </EmptyState>
        </div>
      </View>
    )
  }

  return (
    <View>
      {/* A column, so the notes take the slack under the title. `list` rather
          than `fade`: the moves below are a real edge, so the body needs
          breathing room under its last line, not a 72px dissolve. */}
      <View.Body className="flex flex-col gap-2 pt-3" bottomInset="list">
        {/* The tick rides with the title, as it does on the row this View is
            about — the popover leaves it off because its own row is right
            above it, and this View has no row. Everything else shares the
            title's column, so the body reads as one block past the mark. */}
        <div className="flex w-full flex-1 items-start gap-2">
          {/* `1lh` of the title's own type, so the box centres on its first
              line whatever that line-height is, and stays there when it wraps. */}
          <span className="type-prose-md flex h-[1lh] shrink-0 items-center">
            <ReminderCheckbox
              checked={resolved}
              title={reminder.title}
              onCheckedChange={(checked) => setResolved(reminder.id, checked)}
            />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-2 self-stretch">
            <ReminderTitle
              item={reminder}
              trigger={VIEW_TRIGGER}
              fieldRef={titleRef}
              onArrowDownOut={focusNotes}
              className={resolved ? 'decoration-foreground-placeholder line-through' : undefined}
            />
            {/* Up with the reminder they belong to, not adrift at the foot of
                the notes. Their own margin, so they read as a band between the
                title and the writing rather than another line of it. */}
            <ReminderAttachments item={reminder} pending={attach.pending} className="my-1" />
            <ReminderNotes item={reminder} fieldRef={notesRef} onArrowUpOut={focusTitle} />
          </div>
        </div>
      </View.Body>
      {/* Outside the body, or it would sit above the body's own bottom inset
          rather than at the foot of the View. */}
      <input
        ref={attach.inputRef}
        type="file"
        className="hidden"
        accept="image/*"
        multiple
        onChange={attach.onInputChange}
      />
      <div className="flex shrink-0 flex-wrap items-center gap-2 px-3 pb-3 pt-2">
        <ReminderDetailActions item={reminder} trigger={VIEW_TRIGGER} showsChat attach={attach} />
      </div>
    </View>
  )
}
